import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { getRoleLabel, getRoleLabels, RoleLabels, Roles } from '../auth/rbac'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { api, getApiErrorMessage } from '../lib/api'
import { asCollection } from '../lib/collections'
import { AccountStatusLabel, AvailabilityLabel } from '../lib/labels'
import ListContainer from '../ui/ListContainer'
import PeopleTabs from '../components/PeopleTabs'
import MenuGuideButton from '../components/MenuGuideButton'

const ORG_MEMBERSHIP_MANAGER_ROLES = [
  Roles.OPERATOR,
  Roles.GA_MASTER_ADMIN,
  Roles.GA_MASTER_DEPUTY_ADMIN,
  Roles.GA_LEADER,
  Roles.GA_DEPUTY_LEADER,
  Roles.GA_MANAGER,
  Roles.GA_BRANCH_MANAGER,
  Roles.GA_DEPUTY_BRANCH_MANAGER,
  Roles.GA_OTHER_ADMIN,
  Roles.INSURER_MASTER_ADMIN,
  Roles.INSURER_MASTER_DEPUTY_ADMIN,
  Roles.INSURER_LEADER,
  Roles.INSURER_DEPUTY_LEADER,
  Roles.INSURER_MANAGER,
  Roles.INSURER_BRANCH_MANAGER,
  Roles.INSURER_DEPUTY_BRANCH_MANAGER,
  Roles.INSURER_OTHER_ADMIN,
]

const USER_INLINE_EDITOR_ROLES = [
  Roles.OPERATOR,
  Roles.GA_MASTER_ADMIN,
  Roles.GA_MASTER_DEPUTY_ADMIN,
  Roles.GA_OTHER_ADMIN,
  Roles.GA_LEADER,
  Roles.GA_MANAGER,
  Roles.GA_DEPUTY_LEADER,
  Roles.GA_BRANCH_MANAGER,
  Roles.GA_DEPUTY_BRANCH_MANAGER,
  Roles.INSURER_MASTER_ADMIN,
  Roles.INSURER_MASTER_DEPUTY_ADMIN,
  Roles.INSURER_OTHER_ADMIN,
  Roles.INSURER_LEADER,
  Roles.INSURER_MANAGER,
  Roles.INSURER_DEPUTY_LEADER,
  Roles.INSURER_BRANCH_MANAGER,
  Roles.INSURER_DEPUTY_BRANCH_MANAGER,
]

function membershipRequestedAtLabel(value) {
  if (!value) return '-'
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  } catch {
    return '-'
  }
}

function membershipRequestUserName(row) {
  return row?.userName || row?.name || row?.userEmail || '신청자'
}

function membershipRequestPhoneLabel(row) {
  return row?.userPhoneNumber || row?.phoneNumber || row?.userPhone || row?.mobilePhone || '-'
}

function membershipRequestSentence(row) {
  const requestedAt = membershipRequestedAtLabel(row?.requestedAt)
  const userName = membershipRequestUserName(row)
  const phone = membershipRequestPhoneLabel(row)
  const orgName = row?.orgName || '조직'
  return `[${requestedAt}] ${userName}(${phone}) 님이 ${orgName} 으로 소속 승인 요청을 하였습니다.`
}

export default function UsersPage() {
  const queryClient = useQueryClient()
  const prefetchedAllowedRolesRef = React.useRef(new Set())
  const [q, setQ] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(Math.max(1, Number(searchParams.get('page') || '1')))
  const [size, setSize] = useState(Math.max(1, Number(searchParams.get('size') || '10')))
  const [availabilityStatus, setAvailabilityStatus] = useState(searchParams.get('availabilityStatus') || '')
  // 운영자 전용: 생성자 역할(GA_MANAGER) 기반 필터 (전체 | 제외 | 만)
  const initialCreatorMode = (() => {
    const mode = (searchParams.get('createdByGaManagerMode') || '').toLowerCase()
    if (mode === 'only' || mode === 'exclude') return mode
    // 하위호환: createdByGaManager=true -> only
    if (searchParams.get('createdByGaManager') === 'true') return 'only'
    return ''
  })()
  const [createdByGaManagerMode, setCreatedByGaManagerMode] = useState(initialCreatorMode) // '' | 'exclude' | 'only'
  const [accountStatus, setAccountStatus] = useState(() => {
    const s = (searchParams.get('accountStatus') || '').toUpperCase()
    if (['ACTIVE', 'INACTIVE', 'SUSPENDED', 'PENDING'].includes(s)) return s
    return ''
  })
  const [showInvite, setShowInvite] = useState(false)
  const [inviteOrgId, setInviteOrgId] = useState('')
  const [inviteRoles, setInviteRoles] = useState('GA_PLANNER')
  const [inviteExpiresAt, setInviteExpiresAt] = useState('')
  const [inviteMaxUses, setInviteMaxUses] = useState('1')
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', orgId: '1', roles: 'GA_PLANNER', phoneNumber: '' })
  const [orgOptions, setOrgOptions] = useState([])
  const [inviteOrgCategory, setInviteOrgCategory] = useState('')
  const [inviteOrgL1, setInviteOrgL1] = useState('')
  const [inviteOrgL2, setInviteOrgL2] = useState('')
  const [inviteOrgL3, setInviteOrgL3] = useState('')
  const [createOrgCategory, setCreateOrgCategory] = useState('')
  const [createOrgL1, setCreateOrgL1] = useState('')
  const [createOrgL2, setCreateOrgL2] = useState('')
  const [createOrgL3, setCreateOrgL3] = useState('')
  const navigate = useNavigate()
  const { user, hasAnyRole } = useAuth() || { user: null, hasAnyRole: () => false }

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [orgScopeId, setOrgScopeId] = useState(searchParams.get('orgId') || '')
  const [orgScopeOptions, setOrgScopeOptions] = useState([])
  // 운영자 전용: "전체 조직"을 회원가입과 유사하게 단계별로 선택할 수 있도록 UI 상태를 분리한다.
  const isOperatorUser = hasAnyRole && hasAnyRole([Roles.OPERATOR])
  const actorRolesKey = useMemo(() => {
    if (!Array.isArray(user?.roles)) return ''
    return user.roles.map(String).sort().join('|')
  }, [user?.roles])
  const actorInlineAccessKey = useMemo(() => {
    return [user?.id ?? '', user?.orgId ?? '', actorRolesKey].join(':')
  }, [user?.id, user?.orgId, actorRolesKey])
  const canInlineEditAny = hasAnyRole ? hasAnyRole(USER_INLINE_EDITOR_ROLES) : false
  const isGALeader = hasAnyRole && hasAnyRole([Roles.GA_LEADER])
  const isInsLeader = hasAnyRole && hasAnyRole([Roles.INSURER_LEADER])
  const isLeaderOrOperator = (hasAnyRole && hasAnyRole([Roles.OPERATOR, Roles.GA_LEADER, Roles.GA_MANAGER, Roles.INSURER_LEADER]))
  const [opOrgCategory, setOpOrgCategory] = useState('') // 'GA' | 'INSURER' | ''
  const [opOrgL1, setOpOrgL1] = useState('')
  const [opOrgL2, setOpOrgL2] = useState('')
  const [opOrgL3, setOpOrgL3] = useState('')

  const dq = useDebouncedValue(q, 300)

  const formatDateYmd = React.useCallback(() => {
    const d = new Date()
    const yyyy = String(d.getFullYear())
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}${mm}${dd}`
  }, [])

  const notify = React.useCallback((message, variant = 'info', timeoutMs = 2500) => {
    try {
      if (typeof window !== 'undefined' && window.toast && typeof window.toast.show === 'function') {
        window.toast.show(message, variant, timeoutMs)
        return
      }
    } catch { }
    try { alert(message) } catch { }
  }, [])

  const formatDateTime = React.useCallback((iso) => {
    if (!iso) return '-'
    try {
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return String(iso)
      return d.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    } catch {
      return String(iso)
    }
  }, [])
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 1,
    currentPage: 0,
    size: 10,
    hasNext: false,
    hasPrevious: false,
  })

  // 인라인 편집 상태
  const [rowSavingById, setRowSavingById] = useState({}) // { [id]: boolean }
  const [draftOrgIdById, setDraftOrgIdById] = useState({}) // { [id]: string }
  const [draftRoleById, setDraftRoleById] = useState({}) // { [id]: string }
  const [allowedRoleCodesById, setAllowedRoleCodesById] = useState({}) // { [id]: string[] }
  const [allowedRoleLoadingById, setAllowedRoleLoadingById] = useState({}) // { [id]: boolean }
  const [rowForbiddenById, setRowForbiddenById] = useState({}) // { [id]: boolean }
  const [membershipDecisionSavingById, setMembershipDecisionSavingById] = useState({})

  useEffect(() => {
    prefetchedAllowedRolesRef.current.clear()
    setRowSavingById({})
    setDraftOrgIdById({})
    setDraftRoleById({})
    setAllowedRoleCodesById({})
    setAllowedRoleLoadingById({})
    setRowForbiddenById({})
  }, [actorInlineAccessKey])

  // URL → state 동기화 (같은 /users 라우트에서 search만 바뀌는 케이스 대응)
  useEffect(() => {
    const nextOrgId = searchParams.get('orgId') || ''
    const nextAvail = searchParams.get('availabilityStatus') || ''
    const nextCreatedByGaManagerMode = (() => {
      const mode = (searchParams.get('createdByGaManagerMode') || '').toLowerCase()
      if (mode === 'only' || mode === 'exclude') return mode
      if (searchParams.get('createdByGaManager') === 'true') return 'only'
      return ''
    })()
    const nextAccountStatus = (() => {
      const s = (searchParams.get('accountStatus') || '').toUpperCase()
      if (['ACTIVE', 'INACTIVE', 'SUSPENDED', 'PENDING'].includes(s)) return s
      return ''
    })()
    const nextPage = Math.max(1, Number(searchParams.get('page') || '1'))
    const nextSize = Math.max(1, Number(searchParams.get('size') || '10'))
    if (nextOrgId !== orgScopeId) setOrgScopeId(nextOrgId)
    if (nextAvail !== availabilityStatus) setAvailabilityStatus(nextAvail)
    if (nextCreatedByGaManagerMode !== createdByGaManagerMode) setCreatedByGaManagerMode(nextCreatedByGaManagerMode)
    if (nextAccountStatus !== accountStatus) setAccountStatus(nextAccountStatus)
    if (nextPage !== page) setPage(nextPage)
    if (nextSize !== size) setSize(nextSize)
    // q는 입력 UX 때문에 여기서 강제 동기화하지 않는다.
    // (필요하면 별도 "필터 초기화" 버튼으로 처리)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()])

  useEffect(() => {
    const next = new URLSearchParams()
    if (dq) next.set('q', dq)
    if (orgScopeId) next.set('orgId', String(orgScopeId))
    if (availabilityStatus) next.set('availabilityStatus', String(availabilityStatus))
    if (isOperatorUser && accountStatus) next.set('accountStatus', String(accountStatus))
    if (createdByGaManagerMode) next.set('createdByGaManagerMode', createdByGaManagerMode)
    if (page && page !== 1) next.set('page', String(page))
    if (size && size !== 10) next.set('size', String(size))
    setSearchParams(next, { replace: true })
  }, [dq, orgScopeId, availabilityStatus, accountStatus, createdByGaManagerMode, page, size, setSearchParams, isOperatorUser])

  // 검색어 변경 시 페이지를 1페이지로 초기화
  useEffect(() => {
    setPage(1)
  }, [dq])

  const usersQueryKey = useMemo(
    () => ['users', {
      dq,
      page,
      size,
      orgScopeId,
      availabilityStatus,
      accountStatus: isOperatorUser ? accountStatus : '',
      createdByGaManagerMode,
    }],
    [dq, page, size, orgScopeId, availabilityStatus, accountStatus, createdByGaManagerMode, isOperatorUser],
  )

  const listQuery = useQuery({
    queryKey: usersQueryKey,
    queryFn: async () => {
      const params = {}
      if (dq) params.q = dq
      if (orgScopeId) params.orgId = Number(orgScopeId)
      if (availabilityStatus) params.availabilityStatus = availabilityStatus
      if (isOperatorUser && createdByGaManagerMode) {
        params.createdByGaManagerMode = String(createdByGaManagerMode).toUpperCase() // ONLY | EXCLUDE
      }
      if (isOperatorUser && accountStatus) {
        params.accountStatus = accountStatus
      }
      params.page = page - 1
      params.size = size
      const res = await api.get('/users', { params })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data

      if (payload && Array.isArray(payload.content)) {
        const totalElements = payload.totalElements || payload.content.length || 0
        const totalPages = payload.totalPages || 1
        const number = (payload.number ?? 0)
        const pageSize = payload.size || size
        setPagination({
          totalElements,
          totalPages,
          currentPage: number,
          size: pageSize,
          hasNext: number + 1 < totalPages,
          hasPrevious: number > 0,
        })
      } else if (Array.isArray(payload)) {
        setPagination({
          totalElements: payload.length,
          totalPages: 1,
          currentPage: 0,
          size,
          hasNext: false,
          hasPrevious: false,
        })
      }

      const raw = asCollection(payload)
      // 테이블에서 기대하는 키로 매핑 + 한국어 라벨 적용
      const accountStatusLabel = (s) => AccountStatusLabel[s] || s || '-'
      const availabilityLabel = (s) => AvailabilityLabel[s] || s || '-'
      const roleKorean = (roles) => Array.isArray(roles) ? getRoleLabels(roles).join(', ') : (RoleLabels[roles] || roles || '-')
      return raw.map((u) => ({
        id: u.id,
        name: u.name || u.fullName || u.email?.split('@')[0],
        email: u.email,
        roles: Array.isArray(u.roles) ? u.roles : (u.roles ? [u.roles] : []),
        role: roleKorean(u.roles),
        phoneNumber: u.phoneNumber || '-',
        orgId: (u.orgId != null) ? Number(u.orgId) : null,
        org: u.orgName || u.org || '-',
        orgParent: u.orgParentName || '-',
        orgRoot: u.orgRootName || '-',
        status: u.status || 'ACTIVE',
        statusLabel: accountStatusLabel(u.status || u.statusName),
        availability: u.availabilityStatus || '-',
        availabilityLabel: availabilityLabel(u.availabilityStatus),
        deputy: u.deputyUserName || (u.deputyUserId ? `ID ${u.deputyUserId}` : '-'),
        updatedAt: u.updatedAt || u.updated_at || null,
      }))
    },
    keepPreviousData: true,
  })
  useEffect(() => {
    setItems(listQuery.data || [])
    setLoading(listQuery.isFetching)
    setError(listQuery.isError ? '목록을 불러오지 못했습니다' : null)
  }, [listQuery.data, listQuery.isFetching, listQuery.isError])

  // 조직 범위 옵션 로드
  // - 일반 사용자: 내 조직 + 하위 조직(scope=mine)
  // - 운영자: 전체 조직(scope=all) (요구사항: 전체 사용자 목록에서 모든 조직을 선택 가능해야 함)
  useEffect(() => {
    let ignore = false
    async function loadOrgScope() {
      try {
        const res = await api.get('/orgs/options', { params: { scope: isOperatorUser ? 'all' : 'mine' } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setOrgScopeOptions(Array.isArray(payload) ? payload : [])
      } catch {
        if (!ignore) setOrgScopeOptions([])
      }
    }
    loadOrgScope()
    return () => { ignore = true }
  }, [isOperatorUser, actorInlineAccessKey])

  // 운영자 조직 선택(단계형) 옵션 계산
  const orgScopeById = useMemo(() => {
    const m = new Map()
    for (const o of (orgScopeOptions || [])) {
      if (o && o.id != null) m.set(Number(o.id), o)
    }
    return m
  }, [orgScopeOptions])

  const opGaLevel1Options = useMemo(
    () => (orgScopeOptions || []).filter(o => o && !o.parentOrgId && o.type === 'GA'),
    [orgScopeOptions],
  )

  const opPersonalLevel1Options = useMemo(
    () => (orgScopeOptions || []).filter(o => o && !o.parentOrgId && o.type === 'PERSONAL'),
    [orgScopeOptions],
  )

  // 원수사(INSURER)에서 "1차"는 보험사(회사) 노드로 보이게 한다.
  // - 보험사 카테고리(예: 생명/손해) 루트가 존재하는 모델을 고려하여, 부모는 루트이고 조부모가 없는 노드를 "회사"로 간주한다.
  const opInsurerCompanyOptions = useMemo(() => {
    return (orgScopeOptions || []).filter(o => {
      if (!o || o.type !== 'INSURER') return false
      if (!o.parentOrgId) return false
      const parent = orgScopeById.get(Number(o.parentOrgId))
      if (!parent) return false
      return !parent.parentOrgId
    })
  }, [orgScopeOptions, orgScopeById])

  const opLevel1Options = useMemo(() => {
    if (!opOrgCategory) return []
    if (opOrgCategory === 'GA') return opGaLevel1Options
    if (opOrgCategory === 'INSURER') return opInsurerCompanyOptions
    if (opOrgCategory === 'PERSONAL') return opPersonalLevel1Options
    return []
  }, [opOrgCategory, opGaLevel1Options, opInsurerCompanyOptions, opPersonalLevel1Options])

  const opLevel2Options = useMemo(() => {
    if (!opOrgL1) return []
    const pid = Number(opOrgL1)
    return (orgScopeOptions || []).filter(o => o && Number(o.parentOrgId) === pid)
  }, [opOrgL1, orgScopeOptions])

  const opLevel3Options = useMemo(() => {
    if (!opOrgL2) return []
    const pid = Number(opOrgL2)
    return (orgScopeOptions || []).filter(o => o && Number(o.parentOrgId) === pid)
  }, [opOrgL2, orgScopeOptions])

  // 운영자: URL/orgId 상태가 이미 잡혀있으면 단계형 셀렉트에도 반영(새로고침/뒤로가기 UX)
  useEffect(() => {
    if (!isOperatorUser) return
    if (!orgScopeId) return
    if (!orgScopeOptions || orgScopeOptions.length === 0) return

    const selected = orgScopeById.get(Number(orgScopeId))
    if (!selected) return

    if (selected.type === 'GA') {
      // GA: 루트(1차)까지 parent를 따라 올라간 후, 다시 내려오는 경로로 L1/L2/L3를 채운다.
      const chain = []
      let cur = selected
      while (cur) {
        chain.push(cur)
        if (!cur.parentOrgId) break
        cur = orgScopeById.get(Number(cur.parentOrgId))
      }
      chain.reverse() // root -> selected
      setOpOrgCategory('GA')
      setOpOrgL1(chain[0]?.id != null ? String(chain[0].id) : '')
      setOpOrgL2(chain[1]?.id != null ? String(chain[1].id) : '')
      setOpOrgL3(chain[2]?.id != null ? String(chain[2].id) : '')
      return
    }

    if (selected.type === 'INSURER') {
      // INSURER: "회사 노드"를 L1로 삼는다. (부모는 루트, 조부모 없음)
      const isCompany = (o) => {
        if (!o || o.type !== 'INSURER') return false
        if (!o.parentOrgId) return false
        const p = orgScopeById.get(Number(o.parentOrgId))
        if (!p) return false
        return !p.parentOrgId
      }

      // selected에서 위로 올라가며 회사 노드까지 찾고, 회사->selected 경로를 만든다.
      const chainUp = []
      let cur = selected
      while (cur) {
        chainUp.push(cur)
        if (isCompany(cur)) break
        if (!cur.parentOrgId) break
        cur = orgScopeById.get(Number(cur.parentOrgId))
      }
      const chain = chainUp.reverse() // company -> selected (or root-ish)
      setOpOrgCategory('INSURER')
      // 회사 노드를 찾지 못하면(루트 카테고리 등) L1은 비운다.
      if (!isCompany(chain[0])) {
        setOpOrgL1('')
        setOpOrgL2('')
        setOpOrgL3('')
        return
      }
      setOpOrgL1(chain[0]?.id != null ? String(chain[0].id) : '')
      setOpOrgL2(chain[1]?.id != null ? String(chain[1].id) : '')
      setOpOrgL3(chain[2]?.id != null ? String(chain[2].id) : '')
      return
    }

    if (selected.type === 'PERSONAL') {
      const chain = []
      let cur = selected
      while (cur) {
        chain.push(cur)
        if (!cur.parentOrgId) break
        cur = orgScopeById.get(Number(cur.parentOrgId))
      }
      chain.reverse()
      setOpOrgCategory('PERSONAL')
      setOpOrgL1(chain[0]?.id != null ? String(chain[0].id) : '')
      setOpOrgL2(chain[1]?.id != null ? String(chain[1].id) : '')
      setOpOrgL3(chain[2]?.id != null ? String(chain[2].id) : '')
    }
  }, [isOperatorUser, orgScopeId, orgScopeOptions, orgScopeById])

  const filtered = useMemo(() => items, [items])

  // 가입신청 승인 화면으로 이동 가능한 역할(= /signups 라우트 RequireRole과 동일 컨셉)
  const SIGNUP_APPROVER_ROLES = useMemo(
    () => [
      Roles.OPERATOR,
      Roles.GA_LEADER,
      Roles.GA_MANAGER,
      Roles.INSURER_LEADER,
      Roles.INSURER_BRANCH_MANAGER,
    ],
    [],
  )
  const canApproveSignups = hasAnyRole ? hasAnyRole(SIGNUP_APPROVER_ROLES) : false

  // "내가 승인 가능한 가입신청" 대기 인원 수(가벼운 조회: size=1로 totalElements만 사용)
  const signupPendingQuery = useQuery({
    queryKey: ['signups', 'pendingCount'],
    enabled: Boolean(canApproveSignups),
    queryFn: async () => {
      const res = await api.get('/signups', { params: { page: 0, size: 1 } })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (payload && typeof payload.totalElements === 'number') return payload.totalElements
      if (payload && Array.isArray(payload.content)) return payload.content.length
      const items = payload?.items
      if (Array.isArray(items)) return items.length
      return 0
    },
    staleTime: 30 * 1000,
  })

  const withdrawalPendingQuery = useQuery({
    queryKey: ['users', 'withdrawalPendingCount'],
    enabled: Boolean(isOperatorUser),
    queryFn: async () => {
      const res = await api.get('/users/withdrawal-requests/pending-count')
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const c = payload?.count
      return typeof c === 'number' ? c : Number(c) || 0
    },
    staleTime: 30 * 1000,
  })

  const canManageMembershipRequests = hasAnyRole ? hasAnyRole(ORG_MEMBERSHIP_MANAGER_ROLES) : false
  const membershipRequestsOrgId = useMemo(() => {
    if (!canManageMembershipRequests) return ''
    if (isOperatorUser) return orgScopeId ? String(orgScopeId) : ''
    return user?.orgId != null ? String(user.orgId) : ''
  }, [canManageMembershipRequests, isOperatorUser, orgScopeId, user?.orgId])

  const membershipRequestsQuery = useQuery({
    queryKey: ['orgMembershipRequests', membershipRequestsOrgId],
    enabled: Boolean(canManageMembershipRequests && membershipRequestsOrgId),
    queryFn: async () => {
      const res = await api.get(`/orgs/${membershipRequestsOrgId}/membership-requests`, {
        params: { includeDescendants: true },
        __silent: true,
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      return Array.isArray(payload) ? payload : []
    },
    staleTime: 15 * 1000,
  })

  const childrenOf = React.useCallback(
    (parentId) => orgOptions.filter(o => o.parentOrgId === parentId),
    [orgOptions]
  )

  const lifeRootIds = useMemo(
    () =>
      orgOptions
        .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('생명') || o.name.includes('생보')))
        .map(o => o.id),
    [orgOptions]
  )

  const nonLifeRootIds = useMemo(
    () =>
      orgOptions
        .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('손해') || o.name.includes('화재') || o.name.includes('해상')))
        .map(o => o.id),
    [orgOptions]
  )

  const gaRootIds = useMemo(
    () => orgOptions.filter(o => !o.parentOrgId && o.type === 'GA').map(o => o.id),
    [orgOptions]
  )

  const personalRootIds = useMemo(
    () => orgOptions.filter(o => !o.parentOrgId && o.type === 'PERSONAL').map(o => o.id),
    [orgOptions]
  )

  const inviteLevel1Options = useMemo(() => {
    if (!inviteOrgCategory) return []
    if (inviteOrgCategory === 'GA') {
      return orgOptions.filter(o => gaRootIds.includes(o.id))
    }
    if (inviteOrgCategory === 'INS_LIFE') {
      return orgOptions.filter(o => lifeRootIds.includes(o.parentOrgId))
    }
    if (inviteOrgCategory === 'INS_NONLIFE') {
      return orgOptions.filter(o => nonLifeRootIds.includes(o.parentOrgId))
    }
    if (inviteOrgCategory === 'PERSONAL') {
      return orgOptions.filter(o => personalRootIds.includes(o.id))
    }
    return []
  }, [inviteOrgCategory, orgOptions, gaRootIds, lifeRootIds, nonLifeRootIds, personalRootIds])

  const inviteLevel2Options = useMemo(() => {
    if (!inviteOrgL1) return []
    return childrenOf(Number(inviteOrgL1))
  }, [inviteOrgL1, childrenOf])

  const inviteLevel3Options = useMemo(() => {
    if (!inviteOrgL2) return []
    return childrenOf(Number(inviteOrgL2))
  }, [inviteOrgL2, childrenOf])

  const createLevel1Options = useMemo(() => {
    if (!createOrgCategory) return []
    if (createOrgCategory === 'GA') {
      return orgOptions.filter(o => gaRootIds.includes(o.id))
    }
    if (createOrgCategory === 'INS_LIFE') {
      return orgOptions.filter(o => lifeRootIds.includes(o.parentOrgId))
    }
    if (createOrgCategory === 'INS_NONLIFE') {
      return orgOptions.filter(o => nonLifeRootIds.includes(o.parentOrgId))
    }
    if (createOrgCategory === 'PERSONAL') {
      return orgOptions.filter(o => personalRootIds.includes(o.id))
    }
    return []
  }, [createOrgCategory, orgOptions, gaRootIds, lifeRootIds, nonLifeRootIds, personalRootIds])

  const createLevel2Options = useMemo(() => {
    if (!createOrgL1) return []
    return childrenOf(Number(createOrgL1))
  }, [createOrgL1, childrenOf])

  const createLevel3Options = useMemo(() => {
    if (!createOrgL2) return []
    return childrenOf(Number(createOrgL2))
  }, [createOrgL2, childrenOf])

  // 최종 inviteOrgId / createForm.orgId 동기화
  useEffect(() => {
    const selected = inviteOrgL3 || inviteOrgL2 || inviteOrgL1 || ''
    setInviteOrgId(selected)
  }, [inviteOrgL1, inviteOrgL2, inviteOrgL3])

  useEffect(() => {
    const selected = createOrgL3 || createOrgL2 || createOrgL1 || ''
    setCreateForm(prev => ({ ...prev, orgId: selected || prev.orgId }))
  }, [createOrgL1, createOrgL2, createOrgL3])

  // 조직 옵션 로드 (모달 열릴 때 최신화)
  useEffect(() => {
    let ignore = false
    async function loadOrgs() {
      if (!createOpen && !showInvite && !canInlineEditAny) return
      try {
        // 인라인 "소속 변경"은 본인이 변경 가능한 범위만 노출되어야 한다.
        // - 운영자만 all
        // - 그 외는 mine (내 조직 + 하위)
        const scope = isOperatorUser ? 'all' : 'mine'
        const res = await api.get('/orgs/options', { params: { scope } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        let options = Array.isArray(payload) ? payload : []
        // scope=mine이면 서버에서 이미 제한됨. 별도 보정은 하지 않는다.
        if (!ignore) setOrgOptions(options)
        // 기본값은 비워 두고, 사용자가 직접 선택하게 함
      } catch (e) {
        if (!ignore) setOrgOptions([])
      }
    }
    loadOrgs()
    return () => { ignore = true }
  }, [createOpen, showInvite, canInlineEditAny, isOperatorUser, actorInlineAccessKey])

  // 운영자는 모든 기능 가능(요구사항). 상세 버튼은 운영자/리더에게는 항상 노출한다.
  // 참조가 매 렌더마다 바뀌면 아래 allowed-roles 프리페치 useEffect가 매번 재실행되어
  // 상세 → 목록 복귀 시 요청/렌더 폭주로 탭이 멈춘 것처럼 보일 수 있다.
  const canViewOurOrgUserDetail = React.useCallback((u) => {
    if (!u) return false
    if (hasAnyRole && hasAnyRole([Roles.OPERATOR])) return true
    if (isGALeader || isInsLeader) return true
    if (!canInlineEditAny) return false

    const uid = u?.id != null ? String(u.id) : null
    if (!uid) return false
    if (rowForbiddenById[uid]) return false
    if (Object.prototype.hasOwnProperty.call(allowedRoleCodesById, uid)) {
      const allowed = Array.isArray(allowedRoleCodesById[uid]) ? allowedRoleCodesById[uid] : []
      return allowed.length > 0
    }
    return false
  }, [hasAnyRole, isGALeader, isInsLeader, canInlineEditAny, rowForbiddenById, allowedRoleCodesById])

  const canRenderInlineControls = React.useCallback((u) => {
    return Boolean(u && canInlineEditAny)
  }, [canInlineEditAny])

  const isInlineDisabled = React.useCallback((u) => {
    const uid = u?.id != null ? String(u.id) : null
    const s = u?.status
    // 정책: 비활성/정지 계정은 상태 복구 외 정보 수정 불가(BE에서도 차단)
    if (s === 'INACTIVE' || s === 'SUSPENDED') return true
    if (uid && rowForbiddenById[uid]) return true
    if (uid && rowSavingById[uid]) return true

    // 운영자/대표(리더)는 목록에서 편집이 빈번하므로,
    // allowed-roles 사전 조회가 아직 완료되지 않았더라도(=access info 미확정)
    // UI를 disable로 두지 않는다. (초기 로딩에서 과도한 disable/혼란 방지)
    if (isOperatorUser || isGALeader || isInsLeader) return false

    // 그 외(예: GA_MANAGER 등): 권한(403) 여부를 아직 확정하지 못한 행은 우선 disable로 둔다.
    // (클릭 후 "권한 없음" UX 방지)
    if (uid) {
      const hasAccessInfo =
        rowForbiddenById[uid] === true
        || Object.prototype.hasOwnProperty.call(allowedRoleCodesById, uid)
      if (!hasAccessInfo) return true
    }
    return false
  }, [rowForbiddenById, rowSavingById, allowedRoleCodesById, isOperatorUser, isGALeader, isInsLeader])

  const getEffectiveOrgIdStr = React.useCallback((u) => {
    const did = u?.id != null ? String(u.id) : null
    if (!did) return ''
    const draft = draftOrgIdById[did]
    if (draft != null && String(draft).trim() !== '') return String(draft)
    return (u?.orgId != null) ? String(u.orgId) : ''
  }, [draftOrgIdById])

  const getEffectivePrimaryRoleCode = React.useCallback((u) => {
    const did = u?.id != null ? String(u.id) : null
    if (!did) return ''
    const draft = draftRoleById[did]
    if (draft != null && String(draft).trim() !== '') return String(draft)
    const rs = Array.isArray(u?.roles) ? u.roles.map(String) : []
    // 목록 UI는 "대표 1개"만 선택 UX로 제공
    return rs[0] || ''
  }, [draftRoleById])

  const loadAllowedRolesForUser = React.useCallback(async (userId, orgIdStr) => {
    const uid = String(userId)
    if (!uid) return []

    setAllowedRoleLoadingById(prev => ({ ...prev, [uid]: true }))
    try {
      const qs = orgIdStr ? `?orgId=${Number(orgIdStr)}` : ''
      const res = await api.get(`/users/${uid}/allowed-roles${qs}`, { __silent: true })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const roles = Array.isArray(payload?.roles) ? payload.roles.map(String) : []
      setAllowedRoleCodesById(prev => ({ ...prev, [uid]: roles }))
      setRowForbiddenById(prev => ({ ...prev, [uid]: false }))
      return roles
    } catch (e) {
      if (e?.response?.status === 403) {
        setRowForbiddenById(prev => ({ ...prev, [uid]: true }))
      }
      setAllowedRoleCodesById(prev => ({ ...prev, [uid]: [] }))
      return []
    } finally {
      setAllowedRoleLoadingById(prev => ({ ...prev, [uid]: false }))
      // 프리페치 완료 후에만 키를 넣는다. (요청 전에 넣으면 이펙트 취소/배치 중단 시
      // 일부 행이 영구히 allowedRoleCodesById 없이 남아 GA_MANAGER 화면에서만 회색으로 고정됨)
      const orgKey = orgIdStr != null && String(orgIdStr) !== '' ? String(orgIdStr) : ''
      prefetchedAllowedRolesRef.current.add(`${uid}:${orgKey}`)
    }
  }, [])

  // 목록 로딩 직후: 인라인 편집 가능 여부를 조용히 사전 판별한다.
  // - /users/{id}/allowed-roles 가 403이면 BE canManage 불가 → 해당 행은 disable 처리
  // - 사전 조회는 전역 토스트를 띄우지 않도록 __silent 옵션을 사용한다.
  useEffect(() => {
    const rows = Array.isArray(listQuery.data) ? listQuery.data : []
    const targets = rows
      .filter(u => canRenderInlineControls(u))
      .map(u => ({ id: u?.id, orgId: u?.orgId }))
      .filter(x => x.id != null)

    if (targets.length === 0) return

    // 중복 호출 방지: (userId + orgId) 키 단위로 1회만 prefetch한다.
    // 키는 loadAllowedRolesForUser finally에서만 추가한다(위 주석).
    const need = targets.filter(t => {
      const uid = String(t.id)
      const orgKey = t.orgId != null ? String(t.orgId) : ''
      const key = `${uid}:${orgKey}`
      return !prefetchedAllowedRolesRef.current.has(key)
    })

    if (need.length === 0) return

    let cancelled = false
    ;(async () => {
      // 너무 많은 동시 호출을 피하기 위해 소규모 배치로 순차 처리
      for (let i = 0; i < need.length; i += 5) {
        if (cancelled) return
        const batch = need.slice(i, i + 5)
        await Promise.all(batch.map(t => {
          const orgIdStr = t.orgId != null ? String(t.orgId) : ''
          return loadAllowedRolesForUser(t.id, orgIdStr)
        }))
      }
    })()

    return () => { cancelled = true }
  }, [
    listQuery.data,
    canRenderInlineControls,
    loadAllowedRolesForUser,
    actorInlineAccessKey,
  ])

  const computeNextRoles = React.useCallback((existingRoles, selectedRole, nextOrgIdStr) => {
    const cur = Array.isArray(existingRoles) ? existingRoles.map(String) : []
    const nextRole = selectedRole ? String(selectedRole) : ''

    // org type 기반으로 "편집 대상 역할군"을 좁혀서, 다른 역할(레거시/다중 역할)을 최대한 보존한다.
    const nextOrgId = nextOrgIdStr ? Number(nextOrgIdStr) : null
    const orgType = (nextOrgId != null)
      ? (orgOptions || []).find(o => Number(o?.id) === nextOrgId)?.type
      : null
    const targetPrefix = orgType === 'GA' ? 'GA_' : orgType === 'INSURER' ? 'INSURER_' : null
    const isOrgRole = (r) => String(r).startsWith('GA_') || String(r).startsWith('INSURER_')
    const isEditableOrgRole = (r) => {
      const rr = String(r)
      if (!isOrgRole(rr)) return false
      if (!targetPrefix) return true
      return rr.startsWith(targetPrefix)
    }

    const immutable = new Set()
    // 대표 역할은 비운영자에게는 사실상 immutable(상세화면 정책과 동일 철학)
    if (!isOperatorUser) {
      immutable.add(Roles.GA_LEADER)
      immutable.add(Roles.INSURER_LEADER)
    }

    // 이전 GA_* / INSURER_* 등 "대상 조직과 맞지 않는 조직 역할"은 제거한다.
    // (개인회원 GA_MANAGER → 원수사 이동 시 GA_MANAGER를 preserved에 남기면 BE가 400으로 거부함)
    const preserved = cur.filter((r) => {
      const rr = String(r)
      if (immutable.has(rr)) return true
      if (!isOrgRole(rr)) return true
      if (!targetPrefix) return !isEditableOrgRole(rr)
      return rr.startsWith(targetPrefix)
    })
    const merged = Array.from(new Set([...preserved, nextRole].filter(Boolean)))
    return merged
  }, [orgOptions, isOperatorUser])

  const saveInlineUser = React.useCallback(async (u, { nextOrgIdStr, nextRoleCode } = {}) => {
    if (!u?.id) return
    if (!canRenderInlineControls(u)) return
    if (isInlineDisabled(u)) return

    const uid = String(u.id)
    if (rowSavingById[uid]) return

    const beforeOrgIdStr = (u.orgId != null) ? String(u.orgId) : ''
    const beforeRoleCode = (Array.isArray(u.roles) ? u.roles.map(String)[0] : '') || ''
    const beforeRoles = Array.isArray(u.roles) ? u.roles.map(String) : []
    const beforeUpdatedAt = u.updatedAt || null

    const orgIdStr = (nextOrgIdStr != null) ? String(nextOrgIdStr) : getEffectiveOrgIdStr(u)
    if (!orgIdStr) return

    // 허용 역할은 org 기준으로 갱신
    const allowed = await loadAllowedRolesForUser(uid, orgIdStr)
    if (rowForbiddenById[uid]) return
    let roleCode = (nextRoleCode != null) ? String(nextRoleCode) : getEffectivePrimaryRoleCode(u)
    if (Array.isArray(allowed) && allowed.length > 0 && (!roleCode || !allowed.includes(roleCode))) {
      roleCode = allowed[0]
      setDraftRoleById(prev => ({ ...prev, [uid]: roleCode }))
    }

    const nextRoles = computeNextRoles(beforeRoles, roleCode, orgIdStr)

    setRowSavingById(prev => ({ ...prev, [uid]: true }))
    try {
      await api.patch(`/users/${uid}`, {
        orgId: Number(orgIdStr),
        roles: nextRoles,
      }, { __silent: true })

      // 즉시 UI 반영(정합성은 refetch로 보강)
      const nextUpdatedAt = new Date().toISOString()
      const nextRoleLabel = getRoleLabels(nextRoles).join(', ')

      // 소속 문자열도 orgOptions 기반으로 즉시 갱신 (refetch 없이 UX 유지)
      const orgById = new Map((orgOptions || []).filter(Boolean).map(o => [Number(o.id), o]))
      const computeOrgNames = (id) => {
        const start = orgById.get(Number(id))
        if (!start) return { org: u.org || '-', orgParent: u.orgParent || '-', orgRoot: u.orgRoot || '-' }
        const org = start.name || '-'
        const parent = (start.parentOrgId != null) ? orgById.get(Number(start.parentOrgId)) : null
        const orgParent = parent?.name || '-'
        // root는 operator 화면에서만 의미가 큰데, options에 없으면 기존값 유지
        let rootName = '-'
        try {
          let cur = start
          let safety = 0
          while (cur && cur.parentOrgId != null && safety++ < 20) {
            const p = orgById.get(Number(cur.parentOrgId))
            if (!p) break
            cur = p
          }
          rootName = cur?.name || '-'
        } catch { }
        return { org, orgParent, orgRoot: rootName }
      }
      const orgNames = computeOrgNames(orgIdStr)

      setDraftOrgIdById(prev => ({ ...prev, [uid]: orgIdStr }))
      setDraftRoleById(prev => ({ ...prev, [uid]: roleCode }))

      queryClient.setQueryData(usersQueryKey, (old) => {
        if (!Array.isArray(old)) return old
        return old.map(row => {
          if (!row || String(row.id) !== uid) return row
          return {
            ...row,
            orgId: Number(orgIdStr),
            roles: nextRoles,
            role: nextRoleLabel,
            org: orgNames.org,
            orgParent: orgNames.orgParent,
            orgRoot: orgNames.orgRoot,
            updatedAt: nextUpdatedAt,
          }
        })
      })

      notify('저장 완료', 'success')
    } catch (e) {
      // 실패 시 롤백
      setDraftOrgIdById(prev => ({ ...prev, [uid]: beforeOrgIdStr }))
      setDraftRoleById(prev => ({ ...prev, [uid]: beforeRoleCode }))
      queryClient.setQueryData(usersQueryKey, (old) => {
        if (!Array.isArray(old)) return old
        return old.map(row => {
          if (!row || String(row.id) !== uid) return row
          return {
            ...row,
            orgId: (beforeOrgIdStr ? Number(beforeOrgIdStr) : null),
            roles: beforeRoles,
            role: getRoleLabels(beforeRoles).join(', '),
            updatedAt: beforeUpdatedAt,
          }
        })
      })
      const status = e?.response?.status
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || e?.message
      if (status === 403) notify('권한이 없습니다.', 'error')
      else if (msg) notify(String(msg), 'error')
      else notify('저장 실패', 'error')
    } finally {
      setRowSavingById(prev => ({ ...prev, [uid]: false }))
    }
  }, [
    canRenderInlineControls,
    isInlineDisabled,
    computeNextRoles,
    getEffectiveOrgIdStr,
    getEffectivePrimaryRoleCode,
    loadAllowedRolesForUser,
    notify,
    queryClient,
    rowSavingById,
    rowForbiddenById,
    orgOptions,
    usersQueryKey,
  ])

  const allowedRoleOptions = React.useMemo(() => {
    if (hasAnyRole && hasAnyRole([Roles.OPERATOR])) {
      // 정책: 운영자도 "실사용 역할"만 부여/초대할 수 있도록 옵션을 제한한다.
      // - GA: 대표/관리자/설계사
      // - 원수사: 대표/관리자/매니저/설계사
      return [
        Roles.GA_PLANNER,
        Roles.GA_MANAGER,
        Roles.GA_LEADER,
        Roles.INSURER_PLAN_MANAGER,
        Roles.INSURER_MANAGER,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_LEADER,
      ]
    }
    if (isGALeader) {
      return [Roles.GA_MANAGER, Roles.GA_PLANNER]
    }
    if (isInsLeader) {
      // 원수사 대표는 매니저/설계사 생성까지 허용
      return [Roles.INSURER_MANAGER, Roles.INSURER_PLAN_MANAGER]
    }
    return []
  }, [hasAnyRole, isGALeader, isInsLeader])

  const onInvite = async (e) => {
    e.preventDefault()
    if (!isLeaderOrOperator) return alert('권한이 없습니다')
    try {
      const expiresAt = inviteExpiresAt ? new Date(inviteExpiresAt).toISOString() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      const response = await api.post('/invites', { orgId: parseInt(inviteOrgId), roles: [inviteRoles], expiresAt, maxUses: parseInt(inviteMaxUses) })
      const inviteCode = response.data?.data?.code || response.data?.code
      const inviteUrl = `${window.location.origin}/invites/accept/${inviteCode}`
      try { navigator.clipboard?.writeText?.(inviteUrl) } catch { }
      alert(`초대 링크가 생성되었습니다:\n${inviteUrl}\n(클립보드에 복사됨)`)
      setShowInvite(false)
      setInviteOrgId('')
      setInviteRoles('GA_PLANNER')
      setInviteExpiresAt('')
      setInviteMaxUses('1')
    } catch (err) {
      alert('초대 생성 실패: ' + (err.response?.data?.message || err.message))
    }
  }

  const onCreate = async (e) => {
    e.preventDefault()
    if (!isLeaderOrOperator) return alert('권한이 없습니다')
    try {
      await api.post('/users', { name: createForm.name, email: createForm.email, password: createForm.password, orgId: parseInt(createForm.orgId, 10), roles: [createForm.roles], phoneNumber: createForm.phoneNumber || undefined })
      setCreateOpen(false)
      setCreateForm({ name: '', email: '', password: '', orgId: '1', roles: 'GA_PLANNER', phoneNumber: '' })
      listQuery.refetch()
    } catch (err) {
      alert('사용자 생성 실패: ' + (err.response?.data?.message || err.message))
    }
  }

  const decideMembershipRequest = React.useCallback(async (row, action) => {
    if (!row?.id || !row?.orgId) return
    const approving = action === 'approve'
    const ok = window.confirm(approving ? '소속 신청을 승인하시겠습니까?' : '소속 신청을 반려하시겠습니까?')
    if (!ok) return
    const key = String(row.id)
    setMembershipDecisionSavingById(prev => ({ ...prev, [key]: true }))
    try {
      await api.post(`/orgs/${row.orgId}/membership-requests/${row.id}/${action}`, {
        reason: approving ? '관리자 승인' : '관리자 반려',
      }, { __silent: true })
      notify(approving ? '소속 신청을 승인했습니다.' : '소속 신청을 반려했습니다.', 'success')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['orgMembershipRequests'] }),
        queryClient.invalidateQueries({ queryKey: usersQueryKey }),
      ])
    } catch (e) {
      notify(getApiErrorMessage(e, approving ? '승인에 실패했습니다.' : '반려에 실패했습니다.'), 'error')
    } finally {
      setMembershipDecisionSavingById(prev => ({ ...prev, [key]: false }))
    }
  }, [notify, queryClient, usersQueryKey])

  const header = (
    <div className="p-3 border-b">
      <div className="flex items-center justify_between">
        <div className="text-sm text-slate-600">총 {pagination.totalElements}건</div>
      </div>
    </div>
  )

  const table = (
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="px-4 py-2 text-left">이름</th>
          <th className="hidden md:table-cell px-4 py-2 text-left">이메일</th>
          <th className="hidden md:table-cell px-4 py-2 text-left">전화번호</th>
          <th className="px-4 py-2 text-left">역할</th>
          <th className="px-4 py-2 text-left">소속</th>
          {/* <th className="px-4 py-2 text-left">계정 상태</th> */}
          <th className="hidden px-4 py-2 text-left">근무 상태</th>
          <th className="hidden lg:table-cell px-4 py-2 text-left">최종 업데이트</th>
          {/* <th className="px-4 py-2 text-left">대직자</th> */}
          <th className="px-4 py-2"></th>
        </tr>
      </thead>
      <tbody>
        {filtered.map(u => (
          <tr key={u.id} className="border-t hover:bg-slate-50">
            <td className="px-4 py-2">{u.name}</td>
            <td className="hidden md:table-cell px-4 py-2">{u.email}</td>
            <td className="hidden md:table-cell px-4 py-2">{u.phoneNumber}</td>
            <td className="px-4 py-2">
              {canRenderInlineControls(u) ? (
                <>
                  <select
                    className="w-full rounded border px-2 py-1 text-sm bg-white disabled:opacity-50"
                    value={getEffectivePrimaryRoleCode(u) || ''}
                    onFocus={() => {
                      const orgIdStr = getEffectiveOrgIdStr(u)
                      void loadAllowedRolesForUser(u.id, orgIdStr)
                    }}
                    onChange={(e) => {
                      const v = e.target.value
                      setDraftRoleById(prev => ({ ...prev, [String(u.id)]: v }))
                      void saveInlineUser(u, { nextRoleCode: v })
                    }}
                    disabled={isInlineDisabled(u)}
                    title="역할 변경(즉시 저장)"
                  >
                    {(() => {
                      const uid = String(u.id)
                      const allowed = Array.isArray(allowedRoleCodesById[uid]) ? allowedRoleCodesById[uid] : []
                      const current = Array.isArray(u.roles) ? u.roles.map(String) : []
                      const merged = Array.from(new Set([...allowed, ...current].filter(Boolean)))
                      if (allowedRoleLoadingById[uid] && merged.length === 0) {
                        return <option value="">허용 역할 불러오는 중…</option>
                      }
                      if (merged.length === 0) {
                        return <option value="">{u.role || '역할 없음'}</option>
                      }
                      return merged.map(code => (
                        <option key={code} value={code}>{getRoleLabel(code)}</option>
                      ))
                    })()}
                  </select>
                  {allowedRoleLoadingById[String(u.id)] && (
                    <div className="mt-1 text-[11px] text-slate-400">허용 역할 불러오는 중…</div>
                  )}
                </>
              ) : (
                u.role
              )}
            </td>
            <td className="px-4 py-2">
              {canRenderInlineControls(u) ? (
                <div className="space-y-1">
                  <select
                    className="w-full rounded border px-2 py-1 text-sm bg-white disabled:opacity-50"
                    value={getEffectiveOrgIdStr(u) || ''}
                    onChange={(e) => {
                      const v = e.target.value
                      setDraftOrgIdById(prev => ({ ...prev, [String(u.id)]: v }))
                      void saveInlineUser(u, { nextOrgIdStr: v })
                    }}
                    disabled={isInlineDisabled(u)}
                    title="소속 변경(즉시 저장)"
                  >
                    {(() => {
                      const currentId = (u.orgId != null) ? String(u.orgId) : ''
                      const options = Array.isArray(orgOptions) ? orgOptions : []
                      if (options.length === 0) {
                        return <option value={currentId}>{u.org || '현재 소속'}</option>
                      }
                      return options.map(o => (
                        <option key={o.id} value={String(o.id)}>{o.name} ({o.type})</option>
                      ))
                    })()}
                  </select>
                </div>
              ) : (
                (() => {
                  const viewingAsOperator = Array.isArray(user?.roles) && user.roles.includes('OPERATOR')
                  const parts = []
                  if (viewingAsOperator) {
                    parts.push(u.orgRoot, u.orgParent, u.org)
                  } else {
                    // 루트(GA/보험사) 표시는 운영자만. parent가 root와 동일하면 parent도 숨김.
                    if (u.orgParent && u.orgParent !== '-' && u.orgParent !== u.orgRoot) parts.push(u.orgParent)
                    parts.push(u.org)
                  }
                  return parts.filter(Boolean).filter(p => p !== '-').join(' ')
                })()
              )}
            </td>
            {/* <td className="px-4 py-2">{u.statusLabel}</td> */}
            <td className="hidden px-4 py-2">{u.availabilityLabel}</td>
            <td className="hidden lg:table-cell px-4 py-2">{formatDateTime(u.updatedAt)}</td>
            {/* <td className="px-4 py-2">{u.deputy}</td> */}
            <td className="px-4 py-2 text-right">
              {canViewOurOrgUserDetail(u) ? (
                <button className="text-brand-navy hover:underline" onClick={() => navigate(`/users/${u.id}`)}>상세</button>
              ) : (
                <span className="text-xs text-slate-400">-</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  const footer = (
    <div className="p-3 border-t text-sm text-slate-600 flex items-center justify-between">
      <div>
        페이지 {page} / {Math.max(1, pagination.totalPages)}
      </div>
      <div className="flex items-center gap-2">
        <button
          className="rounded border px-3 py-1 disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
        >이전</button>
        <button
          className="rounded border px-3 py-1 disabled:opacity-50"
          disabled={page >= Math.max(1, pagination.totalPages)}
          onClick={() => setPage(p => p + 1)}
        >다음</button>
        <select
          className="rounded border px-2 py-1"
          value={size}
          onChange={(e) => { setPage(1); setSize(Number(e.target.value)) }}
        >
          <option value={10}>10개</option>
          <option value={20}>20개</option>
          <option value={50}>50개</option>
        </select>
      </div>
    </div>
  )

  const pendingMembershipRequests = Array.isArray(membershipRequestsQuery.data) ? membershipRequestsQuery.data : []
  const showMembershipRequestPanel = Boolean(
    canManageMembershipRequests
    && membershipRequestsOrgId
    && (
      membershipRequestsQuery.isLoading
      || membershipRequestsQuery.isError
      || pendingMembershipRequests.length > 0
    )
  )

  return (
    <section>
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">사용자/담당자</h1>
            <MenuGuideButton slug="users" title="사용자/담당자 가이드" />
          </div>
          <div className="mt-2">
            <PeopleTabs />
          </div>
        </div>
        {isLeaderOrOperator && (
          <div className="flex items-center gap-2">
            <button className="rounded border px-3 py-2 text-sm" onClick={() => setCreateOpen(true)}>+ 사용자 생성</button>
            <button className="rounded border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100" onClick={() => setShowInvite(true)}>+ 초대 링크 생성</button>
          </div>
        )}
      </div>

      {canApproveSignups && (signupPendingQuery.data || 0) > 0 && (
        <div className="mt-3 rounded border bg-amber-50 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-800">
            <span className="font-semibold">가입신청 승인 대기 인원이 있습니다.</span>{' '}
            <span className="text-slate-700">({signupPendingQuery.data}명)</span>
          </div>
          <button
            type="button"
            className="rounded bg-brand-navy text-white px-3 py-2 text-sm"
            onClick={() => navigate('/signups')}
          >
            승인하러 이동
          </button>
        </div>
      )}

      {isOperatorUser && (withdrawalPendingQuery.data || 0) > 0 && (
        <div className="mt-3 rounded border bg-rose-50 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-800">
            <span className="font-semibold">탈퇴신청 승인 대기 인원이 있습니다.</span>{' '}
            <span className="text-slate-700">({withdrawalPendingQuery.data}명)</span>
          </div>
          <button
            type="button"
            className="rounded bg-brand-navy text-white px-3 py-2 text-sm"
            onClick={() => navigate('/withdrawal-approvals')}
          >
            승인하러 이동
          </button>
        </div>
      )}

      {canManageMembershipRequests && isOperatorUser && !membershipRequestsOrgId && (
        <div className="mt-3 rounded border bg-slate-50 px-4 py-3 text-sm text-slate-700">
          조직 필터를 선택하면 해당 조직의 소속 신청을 확인할 수 있습니다.
        </div>
      )}

      {showMembershipRequestPanel && (
        <div className="mt-3 rounded border bg-sky-50 px-4 py-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-slate-800">
              <span className="font-semibold">조직 소속 승인 대기</span>{' '}
              {!membershipRequestsQuery.isLoading && !membershipRequestsQuery.isError ? (
                <span className="text-slate-700">({pendingMembershipRequests.length}명)</span>
              ) : null}
            </div>
            <button
              type="button"
              className="rounded border border-sky-200 bg-white px-3 py-1.5 text-sm text-sky-800 hover:bg-sky-100"
              onClick={() => membershipRequestsQuery.refetch()}
            >
              새로고침
            </button>
          </div>
          {membershipRequestsQuery.isLoading ? (
            <div className="text-sm text-slate-600">소속 신청을 불러오는 중입니다.</div>
          ) : membershipRequestsQuery.isError ? (
            <div className="text-sm text-rose-700">소속 신청을 불러오지 못했습니다.</div>
          ) : (
            <div className="space-y-2">
              {pendingMembershipRequests.map((row) => {
                const saving = Boolean(membershipDecisionSavingById[String(row.id)])
                return (
                  <div key={row.id} className="flex flex-col gap-3 rounded border bg-white px-3 py-3 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-medium leading-6 text-slate-900">
                        {membershipRequestSentence(row)}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
                        disabled={saving}
                        onClick={() => decideMembershipRequest(row, 'reject')}
                      >
                        반려
                      </button>
                      <button
                        type="button"
                        className="rounded bg-brand-navy px-3 py-1.5 text-sm text-white disabled:opacity-60"
                        disabled={saving}
                        onClick={() => decideMembershipRequest(row, 'approve')}
                      >
                        승인
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <input className="w-full md:w-80 rounded border px-3 py-2 text-sm" placeholder="검색: 이름/이메일/역할/소속" value={q} onChange={(e) => setQ(e.target.value)} />
        <select
          className="hidden md:inline-flex rounded border px-3 py-2 text-sm"
          value={availabilityStatus}
          onChange={(e) => { setAvailabilityStatus(e.target.value || ''); setPage(1) }}
          title="근무 상태 필터"
        >
          <option value="">근무 상태(전체)</option>
          <option value="ON_DUTY">근무중</option>
          <option value="VACATION">휴가</option>
          <option value="TRAINING">교육</option>
          <option value="AWAY">부재중</option>
        </select>
        {isOperatorUser && (
          <select
            className="rounded border px-3 py-2 text-sm"
            value={createdByGaManagerMode}
            onChange={(e) => { setCreatedByGaManagerMode(e.target.value || ''); setPage(1) }}
            title="GA 관리자가 만든 사용자 필터(운영자)"
          >
            <option value="">생성자(전체)</option>
            <option value="exclude">GA 관리자가 만든 사용자 제외</option>
            <option value="only">GA 관리자가 만든 사용자만</option>
          </select>
        )}
        {isOperatorUser && (
          <select
            className="rounded border px-3 py-2 text-sm"
            value={accountStatus}
            onChange={(e) => { setAccountStatus(e.target.value || ''); setPage(1) }}
            title="계정 상태 필터(운영자)"
          >
            <option value="">계정 상태(전체)</option>
            <option value="ACTIVE">활성</option>
            <option value="INACTIVE">비활성</option>
            <option value="SUSPENDED">정지</option>
            <option value="PENDING">승인 대기</option>
          </select>
        )}
        {/* 조직 선택 */}
        {isOperatorUser ? (
          <>
            <select
              className="rounded border px-3 py-2 text-sm"
              value={opOrgCategory}
              onChange={(e) => {
                const v = e.target.value || ''
                setOpOrgCategory(v)
                setOpOrgL1('')
                setOpOrgL2('')
                setOpOrgL3('')
                setOrgScopeId('')
                setPage(1)
              }}
              title="구분 선택"
            >
              <option value="">구분(전체)</option>
              <option value="GA">GA</option>
              <option value="INSURER">원수사</option>
              <option value="PERSONAL">개인</option>
            </select>
            <select
              className="rounded border px-3 py-2 text-sm"
              value={opOrgL1}
              onChange={(e) => {
                const v = e.target.value || ''
                setOpOrgL1(v)
                setOpOrgL2('')
                setOpOrgL3('')
                setOrgScopeId(v)
                setPage(1)
              }}
              disabled={!opOrgCategory}
              title="1차 조직 선택"
            >
              <option value="">{opOrgCategory ? '1차 조직(전체)' : '먼저 구분을 선택하세요'}</option>
              {opLevel1Options.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <select
              className="rounded border px-3 py-2 text-sm"
              value={opOrgL2}
              onChange={(e) => {
                const v = e.target.value || ''
                setOpOrgL2(v)
                setOpOrgL3('')
                setOrgScopeId(v || opOrgL1)
                setPage(1)
              }}
              disabled={!opOrgL1}
              title="2차 조직 선택"
            >
              <option value="">{opOrgL1 ? '2차 조직(전체)' : '먼저 1차 조직을 선택하세요'}</option>
              {opLevel2Options.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <select
              className="rounded border px-3 py-2 text-sm"
              value={opOrgL3}
              onChange={(e) => {
                const v = e.target.value || ''
                setOpOrgL3(v)
                setOrgScopeId(v || opOrgL2 || opOrgL1)
                setPage(1)
              }}
              disabled={!opOrgL2}
              title="3차 조직 선택"
            >
              <option value="">{opOrgL2 ? '3차 조직(전체)' : '먼저 2차 조직을 선택하세요'}</option>
              {opLevel3Options.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <button
              type="button"
              className="rounded border px-3 py-2 text-sm"
              onClick={() => {
                setOpOrgCategory('')
                setOpOrgL1('')
                setOpOrgL2('')
                setOpOrgL3('')
                setOrgScopeId('')
                setPage(1)
              }}
              title="조직 필터 초기화"
            >
              전체보기
            </button>
          </>
        ) : (
          <select
            className="rounded border px-3 py-2 text-sm"
            value={orgScopeId}
            onChange={(e) => { setOrgScopeId(e.target.value); setPage(1) }}
            title="조직(내 조직 + 하위)"
          >
            <option value="">전체보기</option>
            {orgScopeOptions.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        )}
        <button className="hidden md:inline-flex rounded border px-3 py-2 text-sm" onClick={async () => {
          try {
            const params = {
              q: dq || undefined,
              orgId: orgScopeId ? Number(orgScopeId) : undefined,
              availabilityStatus: availabilityStatus || undefined,
              createdByGaManagerMode: (isOperatorUser && createdByGaManagerMode) ? String(createdByGaManagerMode).toUpperCase() : undefined,
              accountStatus: (isOperatorUser && accountStatus) ? accountStatus : undefined,
            }
            const res = await api.get('/users/export', { params, responseType: 'blob' })
            const url = URL.createObjectURL(res.data)
            const a = document.createElement('a')
            a.href = url
            a.download = `people_our_users_${formatDateYmd()}.csv`
            a.click()
            URL.revokeObjectURL(url)
          } catch { }
        }}>CSV 내보내기</button>
      </div>

      <div className="mt-4">
        <ListContainer loading={loading} error={error} onRetry={() => listQuery.refetch()} header={header} table={table} footer={footer} />
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
          <form onSubmit={onInvite} className="w-full max-w-sm rounded bg-white p-6 space-y-4">
            <h2 className="text-lg font-semibold">초대 링크 생성</h2>
            <div>
              <label className="block text-sm">조직 (이름 선택)</label>
              <div className="grid grid-cols-1 gap-2">
                <select
                  className="mt-1 w-full rounded border px-3 py-2"
                  value={inviteOrgCategory}
                  onChange={(e) => {
                    const v = e.target.value
                    setInviteOrgCategory(v)
                    setInviteOrgL1('')
                    setInviteOrgL2('')
                    setInviteOrgL3('')
                  }}
                  required
                >
                  <option value="">구분 선택: 생보 / 손보 / GA / 개인</option>
                  {lifeRootIds.length > 0 && <option value="INS_LIFE">생보(생명보험)</option>}
                  {nonLifeRootIds.length > 0 && <option value="INS_NONLIFE">손보(손해보험)</option>}
                  {gaRootIds.length > 0 && <option value="GA">GA</option>}
                  {personalRootIds.length > 0 && <option value="PERSONAL">개인</option>}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={inviteOrgL1}
                  onChange={(e) => {
                    const v = e.target.value
                    setInviteOrgL1(v)
                    setInviteOrgL2('')
                    setInviteOrgL3('')
                  }}
                  disabled={!inviteOrgCategory || inviteLevel1Options.length === 0}
                  required
                >
                  <option value="">{inviteOrgCategory ? '상위 조직 선택' : '먼저 구분을 선택하세요'}</option>
                  {inviteLevel1Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={inviteOrgL2}
                  onChange={(e) => {
                    const v = e.target.value
                    setInviteOrgL2(v)
                    setInviteOrgL3('')
                  }}
                  disabled={inviteLevel2Options.length === 0}
                >
                  <option value="">1차 하위 조직 (선택)</option>
                  {inviteLevel2Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={inviteOrgL3}
                  onChange={(e) => setInviteOrgL3(e.target.value)}
                  disabled={inviteLevel3Options.length === 0}
                >
                  <option value="">2차 하위 조직 (선택)</option>
                  {inviteLevel3Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-slate-500 mt-1">내 조직과 하위 조직만 표시됩니다.</p>
            </div>
            <div>
              <label className="block text-sm">역할</label>
              <select className="mt-1 w-full rounded border px-3 py-2" value={inviteRoles} onChange={(e) => setInviteRoles(e.target.value)}>
                {allowedRoleOptions.map(code => (
                  <option key={code} value={code}>{getRoleLabel(code)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm">만료일 (선택)</label>
              <input className="mt-1 w-full rounded border px-3 py-2" type="datetime-local" value={inviteExpiresAt} onChange={(e) => setInviteExpiresAt(e.target.value)} />
              <p className="text-xs text_gray-500 mt-1">비워두면 7일 후로 설정됩니다</p>
            </div>
            <div>
              <label className="block text-sm">최대 사용 횟수</label>
              <input className="mt-1 w_full rounded border px-3 py-2" type="number" min="1" value={inviteMaxUses} onChange={(e) => setInviteMaxUses(e.target.value)} required />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setShowInvite(false)}>취소</button>
              <button type="submit" className="rounded bg-brand-navy text_white px-3 py-2 text-sm">초대 링크 생성</button>
            </div>
          </form>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
          <form onSubmit={onCreate} className="w-full max-w-sm rounded bg-white p-6 space-y-4">
            <h2 className="text-lg font-semibold">사용자 생성</h2>
            <div>
              <label className="block text-sm">이름</label>
              <input className="mt-1 w-full rounded border px-3 py-2" value={createForm.name} onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))} required />
            </div>
            <div>
              <label className="block text-sm">이메일</label>
              <input type="email" className="mt-1 w-full rounded border px-3 py-2" value={createForm.email} onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))} required />
            </div>
            <div>
              <label className="block text_sm">비밀번호</label>
              <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={createForm.password} onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))} required />
            </div>
            <div>
              <label className="block text-sm">전화번호</label>
              <input className="mt-1 w-full rounded border px-3 py-2" value={createForm.phoneNumber} onChange={(e) => setCreateForm(prev => ({ ...prev, phoneNumber: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm">조직</label>
              <div className="grid grid-cols-1 gap-2">
                <select
                  className="mt-1 w-full rounded border px-3 py-2"
                  value={createOrgCategory}
                  onChange={(e) => {
                    const v = e.target.value
                    setCreateOrgCategory(v)
                    setCreateOrgL1('')
                    setCreateOrgL2('')
                    setCreateOrgL3('')
                  }}
                  required
                >
                  <option value="">구분 선택: 생보 / 손보 / GA / 개인</option>
                  {lifeRootIds.length > 0 && <option value="INS_LIFE">생보(생명보험)</option>}
                  {nonLifeRootIds.length > 0 && <option value="INS_NONLIFE">손보(손해보험)</option>}
                  {gaRootIds.length > 0 && <option value="GA">GA</option>}
                  {personalRootIds.length > 0 && <option value="PERSONAL">개인</option>}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={createOrgL1}
                  onChange={(e) => {
                    const v = e.target.value
                    setCreateOrgL1(v)
                    setCreateOrgL2('')
                    setCreateOrgL3('')
                  }}
                  disabled={!createOrgCategory || createLevel1Options.length === 0}
                  required
                >
                  <option value="">{createOrgCategory ? '상위 조직 선택' : '먼저 구분을 선택하세요'}</option>
                  {createLevel1Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={createOrgL2}
                  onChange={(e) => {
                    const v = e.target.value
                    setCreateOrgL2(v)
                    setCreateOrgL3('')
                  }}
                  disabled={createLevel2Options.length === 0}
                >
                  <option value="">1차 하위 조직 (선택)</option>
                  {createLevel2Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={createOrgL3}
                  onChange={(e) => setCreateOrgL3(e.target.value)}
                  disabled={createLevel3Options.length === 0}
                >
                  <option value="">2차 하위 조직 (선택)</option>
                  {createLevel3Options.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm">역할</label>
              <select className="mt-1 w-full rounded border px-3 py-2" value={createForm.roles} onChange={(e) => setCreateForm(prev => ({ ...prev, roles: e.target.value }))}>
                {allowedRoleOptions.map(code => (
                  <option key={code} value={code}>{getRoleLabel(code)}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setCreateOpen(false)}>취소</button>
              <button type="submit" className="rounded bg-brand-navy text_white px-3 py-2 text-sm">생성</button>
            </div>
          </form>
        </div>
      )}
    </section>
  )
}


